import {useMemo} from 'react'
import {trimDiffLineText, getContentFromLines} from '../helpers/line-helpers'
import type {DiffLine} from '../types'
import {useAnalytics} from '@github-ui/use-analytics'
import {useStableCallback} from '@github-ui/use-stable-callback'
import type {LineRange} from '@github-ui/diffs/types'
import {useGetSelectedLineRange, useGetSplitDiffLinesFromLineRange} from '../stores/selectors'
import {useClientValue} from '@github-ui/use-client-value'
import {useHasCommitRange} from '@github-ui/pull-requests/pull-request-store-selectors'

/**
 * Returns the configuration for the Suggest Changes button within the markdown toolbar
 */
export function useSuggestedChanges(
  line: DiffLine,
  shouldStartNewConversationWithSuggestedChange: boolean | undefined,
) {
  const {sendAnalyticsEvent} = useAnalytics()
  const getSplitDiffLinesFromLineRange = useGetSplitDiffLinesFromLineRange()
  const hasCommitRange = useHasCommitRange()

  const getSelectedLineRange = useGetSelectedLineRange()
  const [isSSR] = useClientValue(() => false, true, [])

  function shouldShowSuggestChangesButtonFromRowRange(rowRange?: LineRange) {
    // Context menu triggers from single line don't have a row range, so fall back to the line
    if (!rowRange) return line.type !== 'DELETION'

    // Check left side lines in the range first
    const diffLinesFromRange = getSplitDiffLinesFromLineRange(rowRange)?.selectedLeftLines
    if (diffLinesFromRange) {
      return !!diffLinesFromRange?.every(diffLine => diffLine !== 'empty-diff-line' && diffLine.type !== 'DELETION')
    }

    const rightSideOnly = rowRange.startOrientation === 'right' && rowRange.endOrientation === 'right'
    if (rightSideOnly) {
      return true
    }
    const singleLine =
      rowRange.startLineNumber === rowRange.endLineNumber && rowRange.startOrientation === rowRange.endOrientation
    if (singleLine) {
      return line.type !== 'DELETION'
    }
  }

  /**
   *
   * Returns the lines that should be suggested for a comment
   *
   * @param diffLines - the right lines belonging to the range for the thread or the new conversation
   * @param line - the current line the cell from which this is called belongs to
   *
   */
  function getSourceContentDiffLinesFromRowRange(rowRange: LineRange | undefined): string | undefined {
    const diffLines = rowRange ? getSplitDiffLinesFromLineRange(rowRange)?.selectedRightLines : []

    if (rowRange && diffLines && diffLines.length > 1) {
      // Don't attempt getting line content with getContentFromLines function as it uses DOMParser, which does not work with Alloy, while in SSR.
      if (isSSR) return ''
      return getContentFromLines(diffLines)
    }

    if (!line) return ''
    // Single line
    const [lineContent] = trimDiffLineText(line.text, line.type)
    return lineContent
  }

  /**
   * Returns the configuration for the Suggest Changes button within the markdown toolbar
   *
   * @param rowRange - the range of lines for the thread or the new conversation
   * @param shouldInsertSuggestedChange - boolean indicating whether to auto insert the suggested change on render
   */
  const configureSuggestedChangesFromLineRange = useStableCallback(function configureSuggestedChangesFromLineRange(
    rowRange?: LineRange,
    shouldInsertSuggestedChange?: boolean,
  ) {
    const showSuggestChangesButton = !!(shouldShowSuggestChangesButtonFromRowRange(rowRange) && !hasCommitRange)

    return {
      isValidSuggestionRange: showSuggestChangesButton,
      showSuggestChangesButton,
      sourceContentFromDiffLines: showSuggestChangesButton
        ? getSourceContentDiffLinesFromRowRange(rowRange)
        : undefined,
      onInsertSuggestedChange: () => {
        sendAnalyticsEvent('diff.add_suggested_change', 'ADD_SUGGESTED_CHANGE_BUTTON')
      },
      shouldInsertSuggestedChange,
    }
  })

  return useMemo(() => {
    return {
      getSelectedLineRange,
      configureSuggestedChangesFromLineRange,
      shouldStartNewConversationWithSuggestedChange,
    }
  }, [configureSuggestedChangesFromLineRange, getSelectedLineRange, shouldStartNewConversationWithSuggestedChange])
}
